import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const CartContext = createContext(null);

const load = () => {
  try {
    return JSON.parse(localStorage.getItem("deo_cart") || "[]");
  } catch {
    return [];
  }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(load);
  const [coupon, setCoupon] = useState(null);
  const [shipping, setShipping] = useState("standard");

  useEffect(() => {
    localStorage.setItem("deo_cart", JSON.stringify(items));
  }, [items]);

  const add = (product, quantity = 1) => {
    if (!product.price || product.price <= 0) return;
    setItems((cur) => {
      const existing = cur.find((i) => i.slug === product.slug);
      const maxStock = product.stock ?? 99;
      if (existing) {
        return cur.map((i) => (i.slug === product.slug ? { ...i, quantity: Math.min(maxStock, i.quantity + quantity) } : i));
      }
      return [...cur, {
        slug: product.slug, brand: product.brand, model: product.model, model_fr: product.model_fr || "", ml: product.ml,
        image_url: product.image_url, image_style: product.image_style,
        price: product.price, discount_price: product.discount_price, stock: maxStock,
        quantity: Math.min(maxStock, quantity),
      }];
    });
  };

  const setQty = (slug, quantity) => {
    setItems((cur) => cur.map((i) => (i.slug === slug ? { ...i, quantity: Math.max(1, Math.min(i.stock || 99, quantity)) } : i)));
  };

  const remove = (slug) => {
    setItems((cur) => cur.filter((i) => i.slug !== slug));
    setCoupon(null);
  };

  const clear = () => {
    setItems([]);
    setCoupon(null);
  };

  const subtotal = useMemo(
    () => items.reduce((s, i) => s + (i.discount_price || i.price) * i.quantity, 0),
    [items]
  );

  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const applyCoupon = async (code) => {
    const d = await api.post("/coupons/validate", { code, subtotal });
    if (d.data.valid) {
      setCoupon({ code: d.data.code, discount: d.data.discount });
    }
    return d.data;
  };

  return (
    <CartContext.Provider value={{ items, add, setQty, remove, clear, subtotal, count, coupon, setCoupon, applyCoupon, shipping, setShipping }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
